/**
 * 视频任务轮询 hook
 *
 * 用法：
 *   const { jobs, addJobIds, removeJob, isAnyActive } = useVideoJobPolling();
 *   // 提交后：
 *   addJobIds(response.jobIds);
 *
 * 内部：
 * - 每 5 秒一轮，刷新所有活跃 jobId 的状态
 * - 进入终态后停止轮询，但保留在 jobs 中（用户切换前都能看到）
 * - 没有活跃任务时不发请求
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { videoApi, type VideoJobDetail } from '../services/videoApi';

const POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'timeout']);

export function useVideoJobPolling() {
  const [jobs, setJobs] = useState<Record<string, VideoJobDetail>>({});
  const trackedIdsRef = useRef<Set<string>>(new Set());
  const intervalRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    const activeIds: string[] = [];
    for (const id of trackedIdsRef.current) {
      const current = jobs[id];
      if (!current || !TERMINAL_STATUSES.has(current.status)) {
        activeIds.push(id);
      }
    }
    if (activeIds.length === 0) return;

    const results = await Promise.allSettled(activeIds.map((id) => videoApi.getJob(id)));
    setJobs((prev) => {
      const next = { ...prev };
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          next[activeIds[idx]] = res.value;
        }
      });
      return next;
    });
  }, [jobs]);

  // 主循环：仅在有活跃任务时运行
  useEffect(() => {
    const hasActive = Object.values(jobs).some((j) => !TERMINAL_STATUSES.has(j.status));
    const hasUntracked = Array.from(trackedIdsRef.current).some((id) => !jobs[id]);
    const shouldRun = hasActive || hasUntracked;

    if (shouldRun && intervalRef.current === null) {
      intervalRef.current = window.setInterval(() => {
        refresh().catch(() => {});
      }, POLL_INTERVAL_MS);
    } else if (!shouldRun && intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobs, refresh]);

  const addJobIds = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    ids.forEach((id) => trackedIdsRef.current.add(id));

    // 立刻拉一次状态填充占位
    const results = await Promise.allSettled(ids.map((id) => videoApi.getJob(id)));
    setJobs((prev) => {
      const next = { ...prev };
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          next[ids[idx]] = res.value;
        }
      });
      return next;
    });
  }, []);

  const removeJob = useCallback((id: string) => {
    trackedIdsRef.current.delete(id);
    setJobs((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    trackedIdsRef.current.clear();
    setJobs({});
  }, []);

  const list = Object.values(jobs).sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const isAnyActive = list.some((j) => !TERMINAL_STATUSES.has(j.status));

  return {
    jobs: list,
    addJobIds,
    removeJob,
    clearAll,
    isAnyActive,
    refresh,
  };
}
